import { IconName } from '@bytel/trilogy-react';

import { LockedPlanValue } from '@helpers/plan-lock';

import { PlanRange, PlanSubTypeId, PlanTypeId } from '@constants/plan';
import { PLAY, TECHNOLOGY } from '@constants/provider';

import { EligibilityOptionType } from './eligibility';
import { IFundingDetail } from './funding';
import { GalliumOffer } from './gallium';
import { Product, ProductType } from './product';

export enum MobilePlanProvenanceType {
    DEFAULT = 'default',
    CART = 'cart',
    OWNED = 'owned',
    URL = 'url',
}

export enum ObligationType {
    MONTHLY_12 = 'monthly12',
    MONTHLY_24 = 'monthly24',
    NONE = 'none',
}

export enum RenewDelayType {
    MONTHLY_12 = 'monthly12',
    MONTHLY_24 = 'monthly24',
}

export interface BasePlanType extends Product {
    obligation?: ObligationType;
    obligationLabel?: string;
    commercialRange?: PlanRange;
}

export interface MobilePlanType extends BasePlanType {
    sexyName?: string;
    dataEnvelope: string;
    subTypeId: PlanSubTypeId;
    dataSize?: number;
    fromAbroad?: string;
    fundings: IFundingDetail[];
    gencode: string;
    hasDoubleSim: boolean;
    hasLowPrice: boolean;
    internationalCall?: string;
    wordingBtv?: string;
    unlimitedCalls?: string;
    isDarwin: boolean;
    price: number;
    isExcluPro: boolean;
    smsMms?: string;
    shortDescription?: string;
    typeId: PlanTypeId;
    roamingEnvelope?: string;
    voiceEnvelope?: string;
    isOwned?: boolean;
    detailsDescription?: string;
    planFlag?: string;
    locked?: LockedPlanValue;
    maxConnections?: string;
    entityId?: string;
    isDefault?: boolean;
    hasSmartChangeEquivalent?: boolean;
    renewalDiscounts?: GalliumOffer[];
    hasRenewalDiscounts?: boolean;
    renewalLabelDataEnvelope?: string;
    obligation: ObligationType;
    obligationLabel: string;
    renewDelay?: RenewDelayType;
    renewDelayLabel?: string;
    mirrorPlans?: {
        swappy?: MobilePlanType;
        fam?: MobilePlanType;
    };
    cmsContent?: {
        addedPepites?: {
            icon?: IconName;
            pepite?: string;
        }[];
        bigBenefit?: string;
        modalCustomDestinationPartial?: string;
    };
}

export interface OptionType extends Product {
    wordingPromo?: string;
    optionCategory?: string;
    smallImage?: string;
    type?: ProductType;
    shortDescription?: string;
    obligation?: string;
}

export type FaiType = BasePlanType & {
    rentalFees: number;
    downRates: string;
    upRates: string;
    technology: TECHNOLOGY;
    play: PLAY;
    categoryType: string;
    rangeNg: string;
    commercialSeries: string;
    commercialPartner?: string;
    nuggets?: FaiTypeNugget[];
    advantages?: FaiTypeAdvantage[];
    speedWording?: string;
    hat?: string;
    options?: Array<FaiTypeOptions>;
    subtitle?: string;
    detailsUrl?: string;
    speedOption?: EligibilityOptionType;
    isDefault: boolean;
    range: string;
    comparator?: FaiTypeComparatorItems[];
};

type FaiTypeComparatorItems = {
    category: string;
    item: string;
    value: string;
    booleanTrueValue: boolean;
};

export type MobilePlanDetailWithProvenanceType = {
    origin: MobilePlanProvenanceType;
    plan: MobilePlanType | null;
};

export type CategoryListType = CategoryPlanType[];

export type CategoryPlanType = {
    id: string;
    name: string;
    description: string | null;
    code: string;
    subtitle: string;
    url: string;
    products: MobilePlanType[];
    _links: {
        detailCategory: {
            href: string;
        };
    };
};

export type FaiTypeNugget = {
    icon?: string;
    image?: string;
    label: string;
    description?: string;
    position?: number;
    showSpeed?: boolean;
};

export type FaiTypeAdvantage = {
    title: string;
    audienceBigOnly: boolean;
    color: string;
    items: FaiTypeAdvantageItem[];
};

export type FaiTypeAdvantageItem = {
    display: 'image' | 'icon';
    text: string;
    icon?: IconName;
    image?: {
        url: string;
    };
};

export type FaiTypeOptions = {
    logo_url: string;
    logo_alt: string;
    label: string;
    position: number;
};

export interface FaiTypeWithEligibility extends FaiType {
    isEligible?: boolean;
}

export type CategoryCodeInfo = {
    range: string;
    rangeNg: string;
    commercialSeries: string;
    technology: TECHNOLOGY;
    shortDescription?: string;
    categories: PlanTypeId[];
    commercialPartner?: string;
};

export type OffersByCategoryCode = {
    [categoryCode: string]: CategoryCodeInfo[];
};

export type PlanType = FaiType | MobilePlanType;
